/* eslint-disable max-lines */
import { useEffect, useMemo, ReactNode } from 'react';
import { useForm, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { css, StyleSheet } from 'aphrodite';
import cn from 'classnames';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import {
  Globe,
  Type,
  AlignLeft,
  Languages,
  Shield,
  Save,
  RotateCcw,
  Mic
} from 'lucide-react';

import Input from '@components/atoms/ui-kit/input';
import Checkbox from '@components/atoms/ui-kit/checkbox';
import Button from '@components/atoms/ui-kit/button';

import { useThemeContext } from '@utils/hooks/use-theme-context';
import { BaseError } from '@utils/types/shared/base';
import { LanguageAndConfigPayload } from '@utils/api/back-office/language-and-config/use-create-language-and-config';
import { useGetLanguageAndConfig } from '@utils/api/back-office/language-and-config/use-get-language-and-config';
import { useUpdateLanguageAndConfig } from '@utils/api/back-office/language-and-config/use-update-language-config';

/* eslint-disable no-restricted-syntax -- Semantic state colors (enabled/disabled/dirty) */
const STATUS_PILL_ENABLED = {
  backgroundColor: 'rgba(34, 197, 94, 0.10)',
  color: '#22C55E'
};
const STATUS_PILL_DISABLED = {
  backgroundColor: 'rgba(239, 68, 68, 0.10)',
  color: '#EF4444'
};
const DIRTY_PILL = {
  backgroundColor: 'rgba(245, 158, 11, 0.10)',
  color: '#F59E0B'
};
/* eslint-enable no-restricted-syntax */

type Props = { languageId: number };

type SectionProps = {
  icon: ReactNode;
  title: string;
  description: string;
  children: ReactNode;
  className?: string;
};

function Section({
  icon,
  title,
  description,
  children,
  className
}: SectionProps) {
  const { getColour } = useThemeContext();

  return (
    <div
      className={cn('rounded-xl border p-5', className)}
      style={{
        backgroundColor: getColour('secondLayer'),
        borderColor: getColour('border')
      }}
    >
      <div className="flex items-start gap-3 mb-4">
        <div
          className="p-2 rounded-2xl shrink-0"
          style={{ backgroundColor: getColour('thirdLayer') }}
        >
          {icon}
        </div>
        <div>
          <h3
            className="text-sm font-semibold"
            style={{ color: getColour('normalText') }}
          >
            {title}
          </h3>
          <p
            className="text-xs mt-0.5"
            style={{ color: getColour('inactiveText') }}
          >
            {description}
          </p>
        </div>
      </div>
      {children}
    </div>
  );
}

export default function UpdateLanguagePanel({ languageId }: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const { data, isLoading } = useGetLanguageAndConfig(languageId);
  const { mutate: updateLanguageConfig, isLoading: isSaving } =
    useUpdateLanguageAndConfig(languageId);

  const schema = Yup.object().shape({
    id: Yup.number(),
    bcp47: Yup.string().required(t('bcp47-value-is-required')),
    key: Yup.string()
      .max(6, t('string-should-have-at-most-6-characters'))
      .required(t('key-value-is-required')),
    name: Yup.string().required(t('name-value-is-required')),
    is_private: Yup.boolean(),
    is_enabled: Yup.boolean(),
    asr_url: Yup.string().optional(),
    asr_model_location: Yup.string().optional(),
    asr_audio_rate: Yup.number()
      .transform((v, ov) => (ov === '' ? undefined : v))
      .typeError(t('asr-audio-rate-must-be-a-number'))
      .optional(),
    punctuation_url: Yup.string().optional(),
    punctuation_model_location: Yup.string().optional(),
    asr_version: Yup.string()
      .max(6, t('string-should-have-at-most-5-characters'))
      .optional(),
    asr_common_version: Yup.string().optional(),
    alignment_language_code: Yup.string().optional(),
    alignment_version: Yup.string().optional(),
    deepl_code: Yup.string().optional()
  });

  const {
    control,
    handleSubmit,
    reset,
    register,
    formState: { errors, isDirty }
  } = useForm<LanguageAndConfigPayload>({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    const r = data?.result;
    if (!r) return;

    reset({
      id: r.id,
      bcp47: r.bcp47 || '',
      key: r.key || '',
      name: r.name || '',
      is_private: r.is_private || false,
      is_enabled: r.is_enabled || false,
      asr_url: r.asr_url || '',
      asr_model_location: r.asr_model_location || '',
      asr_audio_rate: r.asr_audio_rate || undefined,
      punctuation_url: r.punctuation_url || '',
      punctuation_model_location: r.punctuation_model_location || '',
      asr_version: r.asr_version || '',
      asr_common_version: r.asr_common_version || '',
      alignment_language_code: r.alignment_language_code || '',
      alignment_version: r.alignment_version || '',
      deepl_code: r.deepl_code || ''
    });
  }, [data, reset]);

  const onSubmit = (formData: LanguageAndConfigPayload) => {
    updateLanguageConfig(formData, {
      onSuccess: () => {
        toast.success(t('language-updated-successfully'));
      },
      onError: (error: AxiosError<BaseError>) => {
        const errorMessage =
          error.response?.data?.error_description ||
          error?.response?.data.detail ||
          error?.response?.data.error ||
          error?.response?.data.message ||
          t('error-updating-language');
        toast.error(`${t('error-updating-language')}: ${errorMessage}`);
      }
    });
  };

  const styles = useMemo(
    () =>
      StyleSheet.create({
        stickyBar: {
          backgroundColor: getColour('baseLayer'),
          borderTop: `1px solid ${getColour('border')}`
        }
      }),
    [getColour]
  );

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: getColour('activeText'),
            borderTopColor: 'transparent'
          }}
        />
        <p className="text-sm" style={{ color: getColour('inactiveText') }}>
          {t('loading-language-settings')}...
        </p>
      </div>
    );
  }

  const iconColor = getColour('activeText');
  const iconSize = 'h-4 w-4';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="relative">
      {/* Header Card */}
      <div
        className="rounded-xl border p-4 mb-6 mt-2"
        style={{
          backgroundColor: getColour('secondLayer'),
          borderColor: getColour('border')
        }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold"
              style={{
                backgroundColor: getColour('activeText'),
                color: getColour('baseLayer')
              }}
            >
              {data?.result?.key?.substring(0, 2).toUpperCase() || 'LA'}
            </div>
            <div>
              <h2
                className="text-lg font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {data?.result?.name || t('language-configuration')}
              </h2>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                <span
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: getColour('thirdLayer'),
                    color: getColour('inactiveText')
                  }}
                >
                  ID: #{data?.result?.id}
                </span>
                <span
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: getColour('thirdLayer'),
                    color: getColour('inactiveText')
                  }}
                >
                  {data?.result?.bcp47}
                </span>
                <span
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={
                    data?.result?.is_enabled
                      ? STATUS_PILL_ENABLED
                      : STATUS_PILL_DISABLED
                  }
                >
                  {data?.result?.is_enabled ? t('enabled') : t('disabled')}
                </span>
              </div>
            </div>
          </div>

          {isDirty && (
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
              style={DIRTY_PILL}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              {t('unsaved-changes')}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="space-y-4 pb-20">
        {/* Basic Information */}
        <Section
          icon={<Globe className={iconSize} style={{ color: iconColor }} />}
          title={t('basic-information')}
          description={t('core-language-identifiers-and-codes')}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  id="name"
                  label={t('name')}
                  type="text"
                  {...field}
                  placeholder={t('e.g.-dutch')}
                  {...register('name')}
                  error={errors.name?.message}
                />
              )}
            />
            <Controller
              name="key"
              control={control}
              render={({ field }) => (
                <Input
                  id="key"
                  label={t('key')}
                  type="text"
                  {...field}
                  placeholder={t('e.g.-nl')}
                  {...register('key')}
                  error={errors.key?.message}
                />
              )}
            />
            <Controller
              name="bcp47"
              control={control}
              render={({ field }) => (
                <Input
                  id="bcp47"
                  label="BCP-47"
                  type="text"
                  placeholder={t('e.g.-nl-nl')}
                  {...field}
                  {...register('bcp47')}
                  error={errors.bcp47?.message}
                />
              )}
            />
          </div>
        </Section>

        {/* ASR Configuration */}
        <Section
          icon={<Mic className={iconSize} style={{ color: iconColor }} />}
          title={t('asr-configuration')}
          description={t('speech-recognition-endpoints-and-model-settings')}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Controller
              name="asr_url"
              control={control}
              render={({ field }) => (
                <Input
                  id="asr_url"
                  label={t('asr-url')}
                  type="text"
                  {...field}
                  placeholder="https://asr.example.com"
                  {...register('asr_url')}
                  error={errors.asr_url?.message}
                />
              )}
            />
            <Controller
              name="asr_model_location"
              control={control}
              render={({ field }) => (
                <Input
                  id="asr_model_location"
                  label={t('model-location')}
                  type="text"
                  {...field}
                  placeholder="/models/asr/nl"
                  {...register('asr_model_location')}
                  error={errors.asr_model_location?.message}
                />
              )}
            />
            <Controller
              name="asr_audio_rate"
              control={control}
              render={({ field }) => (
                <Input
                  id="asr_audio_rate"
                  label={t('audio-sample-rate')}
                  type="text"
                  {...field}
                  placeholder="16000"
                  {...register('asr_audio_rate')}
                  error={errors.asr_audio_rate?.message}
                />
              )}
            />
            <Controller
              name="asr_version"
              control={control}
              render={({ field }) => (
                <Input
                  id="asr_version"
                  label={t('asr-version')}
                  type="text"
                  {...field}
                  placeholder="v1.0"
                  {...register('asr_version')}
                  error={errors.asr_version?.message}
                />
              )}
            />
            <Controller
              name="asr_common_version"
              control={control}
              render={({ field }) => (
                <Input
                  id="asr_common_version"
                  label={t('common-version')}
                  type="text"
                  {...field}
                  placeholder="v1.0"
                  {...register('asr_common_version')}
                  error={errors.asr_common_version?.message}
                />
              )}
            />
          </div>
        </Section>

        {/* Punctuation Settings */}
        <Section
          icon={<Type className={iconSize} style={{ color: iconColor }} />}
          title={t('punctuation-settings')}
          description={t('automatic-punctuation-model-configuration')}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Controller
              name="punctuation_url"
              control={control}
              render={({ field }) => (
                <Input
                  id="punctuation_url"
                  label={t('punctuation-url')}
                  type="text"
                  {...field}
                  placeholder="https://punctuation.example.com"
                  {...register('punctuation_url')}
                  error={errors.punctuation_url?.message}
                />
              )}
            />
            <Controller
              name="punctuation_model_location"
              control={control}
              render={({ field }) => (
                <Input
                  id="punctuation_model_location"
                  label={t('model-location')}
                  type="text"
                  {...field}
                  placeholder="/models/punctuation/nl"
                  {...register('punctuation_model_location')}
                  error={errors.punctuation_model_location?.message}
                />
              )}
            />
          </div>
        </Section>

        {/* Alignment Settings */}
        <Section
          icon={<AlignLeft className={iconSize} style={{ color: iconColor }} />}
          title={t('alignment-settings')}
          description={t('forced-alignment-language-and-version-configuration')}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Controller
              name="alignment_language_code"
              control={control}
              render={({ field }) => (
                <Input
                  id="alignment_language_code"
                  label={t('language-code')}
                  type="text"
                  {...field}
                  placeholder="nl"
                  {...register('alignment_language_code')}
                  error={errors.alignment_language_code?.message}
                />
              )}
            />
            <Controller
              name="alignment_version"
              control={control}
              render={({ field }) => (
                <Input
                  id="alignment_version"
                  label={t('version')}
                  type="text"
                  {...field}
                  placeholder="v1.0"
                  {...register('alignment_version')}
                  error={errors.alignment_version?.message}
                />
              )}
            />
          </div>
        </Section>

        {/* Translation Settings */}
        <Section
          icon={<Languages className={iconSize} style={{ color: iconColor }} />}
          title={t('translation-settings')}
          description={t('deepl-integration-and-translation-codes')}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Controller
              name="deepl_code"
              control={control}
              render={({ field }) => (
                <Input
                  id="deepl_code"
                  label={t('deepl-code')}
                  type="text"
                  {...field}
                  placeholder="NL"
                  {...register('deepl_code')}
                  error={errors.deepl_code?.message}
                />
              )}
            />
          </div>
        </Section>

        {/* Visibility & Status */}
        <Section
          icon={<Shield className={iconSize} style={{ color: iconColor }} />}
          title={t('visibility-and-status')}
          description={t('control-who-can-access-this-language')}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div
              className="p-4 rounded-2xl border"
              style={{
                backgroundColor: getColour('thirdLayer'),
                borderColor: getColour('border')
              }}
            >
              <Controller
                name="is_enabled"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className="text-sm font-medium"
                        style={{ color: getColour('normalText') }}
                      >
                        {t('language-enabled')}
                      </p>
                      <p
                        className="text-xs mt-0.5"
                        style={{ color: getColour('inactiveText') }}
                      >
                        {t('allow-this-language-to-be-used-for-processing')}
                      </p>
                    </div>
                    <Checkbox
                      id="is_enabled"
                      name={field.name}
                      ref={field.ref}
                      checked={Boolean(field.value)}
                      onChange={e => field.onChange(e.target.checked)}
                      onBlur={field.onBlur}
                      variant="primary"
                    />
                  </div>
                )}
              />
            </div>

            <div
              className="p-4 rounded-2xl border"
              style={{
                backgroundColor: getColour('thirdLayer'),
                borderColor: getColour('border')
              }}
            >
              <Controller
                name="is_private"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className="text-sm font-medium"
                        style={{ color: getColour('normalText') }}
                      >
                        {t('private-language')}
                      </p>
                      <p
                        className="text-xs mt-0.5"
                        style={{ color: getColour('inactiveText') }}
                      >
                        {t('restrict-visibility-to-specific-customers')}
                      </p>
                    </div>
                    <Checkbox
                      id="is_private"
                      name={field.name}
                      ref={field.ref}
                      checked={Boolean(field.value)}
                      onChange={e => field.onChange(e.target.checked)}
                      onBlur={field.onBlur}
                      variant="primary"
                    />
                  </div>
                )}
              />
            </div>
          </div>
        </Section>
      </div>

      {/* Sticky Footer */}
      <div
        className={cn(
          'sticky bottom-0 z-10 py-3 px-4 -mx-4',
          css(styles.stickyBar)
        )}
      >
        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="smoke"
            size="small"
            onClick={() => reset()}
            disabled={!isDirty}
            icon={<RotateCcw className="h-4 w-4" />}
          >
            {t('reset')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="small"
            disabled={isSaving || !isDirty}
            icon={<Save className="h-4 w-4" />}
          >
            {isSaving ? t('saving') : t('save-changes')}
          </Button>
        </div>
      </div>
    </form>
  );
}
